import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Icon, Image, List, Menu } from 'semantic-ui-react';
import cx from 'classnames';
import { isMatch } from 'lodash';

import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import config from '@plone/volto/registry';

import { getNavigationRootPath } from '../../../../helpers/homepageUrl';
import {
  isExternalNavUrl,
  navLinkTo,
  normalizeNavUrl,
} from '../../../../helpers/navUrl';

import logo from '../Logo/Logo.svg';

const messages = defineMessages({
  closeMobileMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openMobileMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  bookAppointment: {
    id: 'Book appointment',
    defaultMessage: 'DEMANAR HORA',
  },
});

const NavLink = ({ url, className, children }) => {
  const href = navLinkTo(url);

  if (isExternalNavUrl(url)) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
};

NavLink.propTypes = {
  url: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

const Navigation = ({ pathname, footer }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const items = useSelector((state) => state.navigation.items);
  const lang = useSelector((state) => state.intl.locale);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigationRootPath = getNavigationRootPath(pathname, lang);
  const navigationRootUrls = [navigationRootPath, `${navigationRootPath}/`];
  const visibleItems = items.filter(
    (item) => !navigationRootUrls.includes(normalizeNavUrl(item.url)),
  );

  useEffect(() => {
    dispatch(getNavigation(navigationRootPath, config.settings.navDepth || 2));
  }, [dispatch, navigationRootPath]);

  const isActive = (url) => {
    const normalized = normalizeNavUrl(url);
    return (
      (normalized === '' && pathname === '/') ||
      (normalized !== '' && isMatch(pathname.split('/'), normalized.split('/')))
    );
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((open) => !open);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  if (footer) {
    return (
      <>
        {visibleItems.map((item, index) => (
          <div key={index} className="footer-menu-group-container">
            {item.items && item.items.length ? (
              <>
                <span className={isActive(item.url) ? 'item active' : 'item'}>
                  {item.title}
                </span>
                <List>
                  {item.items.map((subitem, subindex) => (
                    <List.Item key={subindex}>
                      <NavLink
                        url={subitem.url}
                        className={
                          isActive(subitem.url) ? 'item active' : 'item'
                        }
                      >
                        {subitem.title}
                      </NavLink>
                    </List.Item>
                  ))}
                </List>
              </>
            ) : (
              <NavLink
                url={item.url}
                className={isActive(item.url) ? 'item active' : 'item'}
              >
                {item.title}
              </NavLink>
            )}
          </div>
        ))}
      </>
    );
  }

  return (
    <nav className="navigation">
      <div className="hamburger-wrapper mobile only">
        <button
          className={cx('hamburger hamburger--collapse', {
            'is-active': isMobileMenuOpen,
          })}
          title={
            isMobileMenuOpen
              ? intl.formatMessage(messages.closeMobileMenu)
              : intl.formatMessage(messages.openMobileMenu)
          }
          type="button"
          onClick={toggleMobileMenu}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        </button>
      </div>
      <Menu
        stackable
        pointing
        secondary
        className={
          isMobileMenuOpen
            ? 'open'
            : 'tablet computer large screen widescreen only'
        }
        onClick={closeMobileMenu}
      >
        <div className="mobile only">
          <div className="mobile-menu-header">
            <Image className="mobile only" src={logo} alt="" />
            <div className="hamburger-wrapper mobile only">
              <button
                className={cx('hamburger hamburger--collapse', {
                  'is-active': isMobileMenuOpen,
                })}
                title={
                  isMobileMenuOpen
                    ? intl.formatMessage(messages.closeMobileMenu)
                    : intl.formatMessage(messages.openMobileMenu)
                }
                type="button"
                onClick={toggleMobileMenu}
              >
                <span className="hamburger-box">
                  <span className="hamburger-inner" />
                </span>
              </button>
            </div>
          </div>
        </div>
        {visibleItems.map((item) => (
          <div
            key={normalizeNavUrl(item.url) || item.title}
            className="header-menu-group-item"
          >
            {item.items && item.items.length ? (
              <>
                <span className={isActive(item.url) ? 'item active' : 'item'}>
                  {item.title}
                </span>
                <List>
                  {item.items.map((subitem, subindex) => (
                    <NavLink key={subindex} url={subitem.url}>
                      <List.Item
                        className={isActive(subitem.url) ? 'active' : ''}
                      >
                        {subitem.title}
                        <Icon name="angle right" className="mobile only" />
                      </List.Item>
                    </NavLink>
                  ))}
                </List>
              </>
            ) : (
              <NavLink
                url={item.url}
                className={isActive(item.url) ? 'item active' : 'item'}
              >
                {item.title}
              </NavLink>
            )}
          </div>
        ))}
        <div className="header-menu-group-item mobile only">
          <a
            href="https://espaipacient.clinicasantjosep.cat/"
            target="_blank"
            rel="noopener noreferrer"
            className="item"
          >
            {intl.formatMessage(messages.bookAppointment)}
          </a>
        </div>
      </Menu>
    </nav>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
  footer: PropTypes.bool,
};

Navigation.defaultProps = {
  footer: false,
};

export default Navigation;
